
import { Component, Vue } from 'vue-property-decorator';
import { Action } from 'vuex-class';
import { KIOSK_FIELD_ROLES, MAX_INDIVIDUAL_LOCATIONS } from '@/utils/constants';
import { toErrorMessage } from '@/utils/errorHandling';
import Toast from '@/utils/toast';
import FullPageSpinner from '@/components/shared/suites/FullPageSpinner.vue';
import TextButton from '@/components/shared/suites/TextButton.vue';
import UserForm from '@/components/user-management/UserForm.vue';
import LocationAssignments from '@/components/user-management/LocationAssignments.vue';

const namespace: string = 'users';

@Component<UserProfile>({
	components: {
		FullPageSpinner,
		LocationAssignments,
		TextButton,
		UserForm
	}
})
export default class UserProfile extends Vue {
	@Action('fetchUserById', { namespace }) private fetchUserById!: (userId: string) => Promise<Auth0User>;
	@Action('updateUser', { namespace }) private updateUser!: (params: { userId: string; payload: UpdateUserPayload }) => Promise<void>;
	@Action('updateUserPassword', { namespace }) private updateUserPassword!: (params: { userId: string; password: string }) => Promise<void>;

	$refs!: { form: UserForm; };

	private isLoading: boolean = true;
	private isSubmitting: boolean = false;
	private isUpdatingPassword: boolean = false;
	private readonly maxIndividualLocations: number = MAX_INDIVIDUAL_LOCATIONS;
	private formValue: UserFormValue = {
		email: '',
		password: '',
		role: '',
		kioskNumber: '',
		pins: ''
	};
	private locationAssignments: LocationAssignment[] = [];
	private locationsPayload: LocationAssignmentsPayload = { groups: [], restaurants: [] };

	private async created(): Promise<void> {
		try {
			const user = await this.fetchUserById(this.$route.params.user_id);

			this.formValue = {
				email: user.email,
				password: '',
				role: user.app_metadata?.role ?? '',
				kioskNumber: user.app_metadata?.kiosk_number ?? '',
				pins: (user.app_metadata?.pins ?? []).join(',')
			};
			this.locationAssignments = user.location_assignments ?? [];
			this.isLoading = false;
		}
		catch (error) {
			Toast.error(this.$t('multi_location_settings.user_management.profile.error_loading', { errorMessage: toErrorMessage(error) }));
			this.$router.push('/multi-location-settings/user-management').catch(() => {});
		}
	}

	/**
	 * Saves changes and updates the user.
	 *
	 * @return {Promise<void>}
	 */
	private async saveChanges(): Promise<void> {
		const isValid = await this.$refs.form.validate();
		if (!isValid) {
			return;
		}

		// app_metadata.restaurants limit check
		if (this.locationsPayload.restaurants.length > this.maxIndividualLocations) {
			Toast.error(this.$t('multi_location_settings.user_management.location_assignments.limit_error', { limit: this.maxIndividualLocations }));
			return;
		}

		const payload: UpdateUserPayload = {
			email: this.formValue.email,
			role: this.formValue.role,
			groups: this.locationsPayload.groups,
			restaurants: this.locationsPayload.restaurants
		};
		// Staff-only values
		if (KIOSK_FIELD_ROLES.includes(this.formValue.role)) {
			payload.kiosk_number = this.formValue.kioskNumber;
			payload.pins = this.formValue.pins
				? this.formValue.pins.split(',').map(p => p.trim()).filter(p => p.length)
				: undefined;
		}

		this.isSubmitting = true;
		try {
			await this.updateUser({ userId: this.$route.params.user_id, payload });
			Toast.success(this.$t('multi_location_settings.user_management.profile.success'));
			this.$router.push('/multi-location-settings/user-management').catch(() => {});
		}
		catch (error) {
			Toast.error(this.$t('multi_location_settings.user_management.profile.error_saving', { errorMessage: toErrorMessage(error) }));
			this.isSubmitting = false;
		}
	}

	/**
	 * Updates the user's password.
	 *
	 * @param {string} password
	 * @return {Promise<void>}
	 */
	private async updatePassword(password: string): Promise<void> {
		this.isUpdatingPassword = true;
		try {
			await this.updateUserPassword({ userId: this.$route.params.user_id, password });
			Toast.success(this.$t('multi_location_settings.user_management.profile.password_updated'));
			this.$refs.form.clearPassword();
		}
		catch (error) {
			Toast.error(this.$t('multi_location_settings.user_management.profile.error_updating_password', { errorMessage: toErrorMessage(error) }));
		}
		finally {
			this.isUpdatingPassword = false;
		}
	}
}
